<script>
	export const prerender = true;

	const plans = [
		{
			name: "Foundation",
			price: "$39",
			note: "/month",
			subnote: "after 7-day free access",
			description:
				"Start with a focused web foundation, no external dependencies.",
			features: [
				"Up to 2 dynamic pages",
				"Up to 100 static pages",
				"No external integrations",
				"Hosting control: OS Studio platform hosting only",
				"Deployment control: managed direct deployment pipeline",
			],
			cta: "Start 7-Day Trial",
			href: "/register?plan=trial",
			highlight: false,
		},
		{
			name: "Connected",
			price: "$129",
			note: "/month",
			subnote: "",
			description:
				"Add essential integrations for your first live business workflow.",
			features: [
				"Up to 5 dynamic pages",
				"Up to 1,000 static pages",
				"Up to 2 external system integrations",
				"Integration validation and retries",
				"Hosting control: choose 1 provider (AWS or Azure or GCP)",
				"Deployment control: single-provider release controls",
				"Basic production monitoring",
			],
			cta: "Choose Connected",
			href: "/register?plan=starter",
			highlight: false,
		},
		{
			name: "Orchestrated",
			price: "$799",
			note: "/month",
			subnote: "",
			description:
				"For automation-heavy operations across multiple systems.",
			features: [
				"Up to 12 dynamic pages",
				"Up to 3,000 static pages",
				"Up to 5 external system integrations",
				"20+ service connectors: CRM, ERP, payments, support, analytics",
				"Advanced agent workflows and approvals",
				"Hosting control: deploy across AWS, Azure, and GCP",
				"Deployment control: multi-cloud rollout and rollback controls",
				"Priority support with managed ops",
			],
			cta: "Lets Orchestrate",
			href: "/register?plan=professional",
			highlight: true,
		},
		{
			name: "Omnichannel",
			price: "Custom",
			note: "annual contract",
			subnote: "",
			description:
				"Expand beyond web with governed, multi-channel delivery.",
			features: [
				"Can scale up to 25+ dynamic pages",
				"Can scale up to 10,000+ static pages",
				"Up to 10 external system integrations",
				"Hosting control: multi-cloud and hybrid options",
				"Deployment control: governed enterprise release controls",
				"Optional mobile apps (iOS/Android)",
				"Optional desktop apps (macOS/Windows)",
				"SLA, compliance, and dedicated architecture support",
			],
			cta: "Contact Sales",
			href: "/contact?plan=enterprise",
			highlight: false,
		},
	];
</script>

<svelte:head>
	<title>Pricing | OS Studio</title>
	<meta
		name="description"
		content="Scope-based pricing for OS Studio: web foundation, integrations, orchestration, and omnichannel expansion."
	/>
</svelte:head>

<section
	class="relative isolate overflow-hidden bg-gradient-to-br from-[#0b0716] via-[#21103d] to-[#110922] py-20 text-white md:py-24 min-h-screen"
>
	<div class="absolute inset-0">
		<div
			class="absolute left-1/2 top-[-10rem] h-[30rem] w-[52rem] -translate-x-1/2 rounded-full bg-purple-500/30 blur-3xl"
		></div>
		<div
			class="absolute bottom-[-8rem] right-[-7rem] h-96 w-96 rounded-full bg-indigo-500/25 blur-3xl"
		></div>
	</div>
	<div
		class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[size:36px_36px] opacity-20"
	></div>

	<div class="relative container mx-auto max-w-7xl px-4">
		<div class="mx-auto mb-14 max-w-3xl text-center">
			<p
				class="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-purple-200"
			>
				Pricing
			</p>
			<h1 class="mb-4 text-4xl font-semibold leading-tight md:text-6xl">
				Choose a plan by delivery scope, not by arbitrary limits
			</h1>
			<p class="text-lg leading-relaxed text-purple-100">
				Start with core web delivery, then scale by integration depth
				and channel expansion for production automation.
			</p>
		</div>

		<div class="grid gap-6 lg:grid-cols-4">
			{#each plans as plan}
				<div
					class={`rounded-3xl border p-7 backdrop-blur h-full flex flex-col ${
						plan.highlight
							? "border-purple-300/70 bg-gradient-to-b from-purple-500/20 to-indigo-500/15 shadow-xl shadow-purple-900/40"
							: "border-white/20 bg-white/10"
					}`}
				>
					{#if plan.highlight}
						<p
							class="mb-4 inline-flex rounded-full border border-purple-200/70 bg-purple-400/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-purple-100"
						>
							Most Popular
						</p>
					{/if}
					<h2 class="text-2xl font-semibold">{plan.name}</h2>
					<p class="mt-2 min-h-[56px] text-sm text-purple-100">
						{plan.description}
					</p>
					<div class="my-6 min-h-[72px]">
						<div class="flex items-baseline gap-2">
							<span class="text-4xl font-semibold"
								>{plan.price}</span
							>
							<span
								class="whitespace-nowrap pb-1 text-sm text-purple-100"
								>{plan.note}</span
							>
						</div>
						{#if plan.subnote}
							<p class="mt-1 text-xs text-purple-200/90">
								{plan.subnote}
							</p>
						{/if}
					</div>

					<ul class="mb-8 flex-1 space-y-3 text-sm text-purple-50">
						{#each plan.features as feature}
							<li class="flex items-start gap-2">
								<span class="mt-1 text-purple-200">✓</span>
								<span>{feature}</span>
							</li>
						{/each}
					</ul>

					<a
						href={plan.href}
						class={`block rounded-xl px-4 py-3 text-center text-sm font-semibold transition-all ${
							plan.highlight
								? "bg-primary text-white hover:bg-primary-400 shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] hover:scale-105"
								: "border border-white/10 bg-white/5 text-white backdrop-blur-sm hover:bg-white/10 hover:border-white/20 hover:text-white"
						}`}
					>
						{plan.cta}
					</a>
				</div>
			{/each}
		</div>

		<div
			class="mt-14 rounded-3xl border border-white/20 bg-white/10 p-8 md:p-10"
		>
			<h3 class="mb-6 text-2xl font-semibold">Included in every plan</h3>
			<div class="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
				<div
					class="rounded-2xl border border-white/15 bg-slate-950/50 p-5"
				>
					<p class="mb-2 text-lg">Agentic blueprinting</p>
					<p class="text-sm text-purple-100">
						AI-generated system blueprint with role model, data map,
						and release stages.
					</p>
				</div>
				<div
					class="rounded-2xl border border-white/15 bg-slate-950/50 p-5"
				>
					<p class="mb-2 text-lg">Secure delivery</p>
					<p class="text-sm text-purple-100">
						Built-in guardrails for auth, data handling, and
						auditability.
					</p>
				</div>
				<div
					class="rounded-2xl border border-white/15 bg-slate-950/50 p-5"
				>
					<p class="mb-2 text-lg">Deployment</p>
					<p class="text-sm text-purple-100">
						Production-ready pipelines for cloud deployment and
						release control.
					</p>
				</div>
				<div
					class="rounded-2xl border border-white/15 bg-slate-950/50 p-5"
				>
					<p class="mb-2 text-lg">Production observability</p>
					<p class="text-sm text-purple-100">
						Health metrics, incident visibility, and performance
						tracking.
					</p>
				</div>
			</div>

			<div
				class="mt-8 rounded-2xl border border-purple-300/30 bg-purple-500/10 p-5"
			>
				<p
					class="text-sm font-semibold uppercase tracking-[0.12em] text-purple-100"
				>
					Optional Add-Ons
				</p>
				<p class="mt-2 text-sm text-purple-100">
					Mobile and desktop apps are offered as scoped add-ons in
					Omnichannel plans, based on feature parity, release cadence,
					and support coverage.
				</p>
			</div>
		</div>
	</div>
</section>
